import { Injectable } from '@angular/core';
import { EventFound } from '@smatchy/sdk';

/**
 * The lines an activity's Instagram story shows, already formatted in French.
 * Kept apart from the drawing so the wording is unit-testable without a canvas.
 */
export interface StoryData {
  sport: string;
  title: string;
  dateLine: string;
  timeLine: string;
  place: string;
  levelLine: string;
  fillLine: string;
  link: string;
}

const WIDTH = 1080;
const HEIGHT = 1920;
const MARGIN = 96;

// Charte graphique Smatchy 2026.
const COLORS = {
  bgTop: '#1A4F6A', // Midnight Blue
  bgBottom: '#123A50', // Midnight Blue, darkened for depth
  accent: '#F5A623', // Smatchy Orange
  white: '#FFFFFF',
  soft: 'rgba(255,255,255,0.82)',
};

const LOGO_SRC = 'assets/layout/images/logo-white.svg';

/**
 * Turns an upcoming activity into a ready-to-share Instagram story image
 * (1080×1920 PNG) generated in the browser — no server, no stored asset.
 * Downloaded like the CSV exports elsewhere in the drawer.
 */
@Injectable({ providedIn: 'root' })
export class InstagramStoryService {
  /** French wording for every line, from the raw activity. */
  toStoryData(event: EventFound): StoryData {
    const start = new Date(event.startDate);

    const dateLine = new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(start);

    const timeLine = `${start.getHours()}h${start.getMinutes().toString().padStart(2, '0')}`;

    // Fill excludes the organiser, everywhere: they sit in both the attendees
    // and the capacity, so both drop by one.
    const partners = Math.max((event.nbAttendees ?? 0) - 1, 0);
    const capacity = Math.max((event.maxNbAttendees ?? 0) - 1, 0);
    const remaining = Math.max(capacity - partners, 0);
    let fillLine: string;
    if (capacity <= 0) {
      fillLine = 'Rejoins l’activité';
    } else if (remaining <= 0) {
      fillLine = 'Complet';
    } else if (partners === 0) {
      fillLine = 'Sois le premier à rejoindre';
    } else {
      fillLine = `Il reste ${remaining} place${remaining > 1 ? 's' : ''}`;
    }

    const levelLine = event.acceptAnyLvl ? 'Tous niveaux' : `Niveau ${event.level}`;
    // The address is often "Venue, street, postcode City"; the last comma-part
    // is the most story-worthy, and the whole thing is kept when there is no comma.
    const parts = (event.locationAddress || event.location?.name || '').split(',').map((p) => p.trim());
    const place = parts[parts.length - 1] || event.locationAddress || '';

    return {
      sport: (event.sportTitle || '').toUpperCase(),
      title: event.title || '',
      dateLine: dateLine.charAt(0).toUpperCase() + dateLine.slice(1),
      timeLine,
      place,
      levelLine,
      fillLine,
      link: 'smatchy.app',
    };
  }

  /** Renders the story to a PNG blob. Fonts are awaited so the canvas isn't drawn in a fallback face. */
  async renderStoryBlob(data: StoryData): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }

    await this.ensureFonts();
    const logo = await this.loadLogo();
    this.draw(ctx, data, logo);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
        'image/png',
      );
    });
  }

  /** Builds the story for the activity and triggers a download. */
  async downloadStory(event: EventFound): Promise<void> {
    const data = this.toStoryData(event);
    const blob = await this.renderStoryBlob(data);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `story-${this.slug(event.title || 'activite')}.png`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private async ensureFonts(): Promise<void> {
    try {
      await Promise.all([
        document.fonts.load('800 90px Poppins'),
        document.fonts.load('700 40px Poppins'),
        document.fonts.load('600 44px Montserrat'),
      ]);
      await document.fonts.ready;
    } catch {
      // A missing font only means a fallback face; never block the download.
    }
  }

  /** The white Smatchy logo, or null so the header falls back to the wordmark. */
  private loadLogo(): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = LOGO_SRC;
    });
  }

  private draw(ctx: CanvasRenderingContext2D, data: StoryData, logo: HTMLImageElement | null): void {
    // Background
    const bg = ctx.createLinearGradient(0, 0, 0, HEIGHT);
    bg.addColorStop(0, COLORS.bgTop);
    bg.addColorStop(1, COLORS.bgBottom);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // Logo (white kangaroo + wordmark), or the wordmark as a fallback.
    ctx.textBaseline = 'alphabetic';
    if (logo && logo.width > 0) {
      const h = 168;
      const w = (logo.width / logo.height) * h;
      ctx.drawImage(logo, MARGIN, 96, w, h);
      // The wordmark beside the mark, so the lockup reads for non-users too.
      ctx.fillStyle = COLORS.white;
      ctx.font = '800 62px Poppins, Montserrat, sans-serif';
      ctx.fillText('smatchy', MARGIN + w + 28, 96 + h / 2 + 22);
    } else {
      ctx.fillStyle = COLORS.white;
      ctx.font = '800 68px Poppins, Montserrat, sans-serif';
      ctx.fillText('smatchy', MARGIN, 200);
    }

    // Sport pill
    const pillY = 400;
    ctx.font = '700 40px Poppins, Montserrat, sans-serif';
    const pillText = data.sport || 'ACTIVITÉ';
    const pillW = ctx.measureText(pillText).width + 72;
    this.roundRect(ctx, MARGIN, pillY, pillW, 84, 42);
    ctx.fillStyle = COLORS.accent;
    ctx.fill();
    ctx.fillStyle = COLORS.white;
    ctx.fillText(pillText, MARGIN + 36, pillY + 56);

    // Title, wrapped
    ctx.fillStyle = COLORS.white;
    ctx.font = '800 96px Poppins, Montserrat, sans-serif';
    const titleLines = this.wrap(ctx, data.title, WIDTH - MARGIN * 2, 3);
    let ty = 640;
    for (const line of titleLines) {
      ctx.fillText(line, MARGIN, ty);
      ty += 112;
    }

    // Meta rows
    const rows = [
      ['📅', data.dateLine],
      ['🕐', data.timeLine],
      ['📍', data.place],
      ['🎯', data.levelLine],
    ].filter(([, value]) => Boolean(value));
    let my = Math.max(ty + 80, 1120);
    ctx.font = '600 46px Montserrat, sans-serif';
    for (const [icon, value] of rows) {
      ctx.fillStyle = COLORS.white;
      ctx.fillText(icon, MARGIN, my);
      ctx.fillStyle = COLORS.soft;
      ctx.fillText(this.ellipsize(ctx, value, WIDTH - MARGIN * 2 - 90), MARGIN + 90, my);
      my += 82;
    }

    // Fill highlight
    ctx.fillStyle = COLORS.accent;
    ctx.font = '800 64px Poppins, Montserrat, sans-serif';
    ctx.fillText(data.fillLine, MARGIN, 1560);

    // CTA + link, bottom
    ctx.fillStyle = COLORS.white;
    ctx.font = '800 58px Poppins, Montserrat, sans-serif';
    ctx.fillText('Rejoins-nous', MARGIN, 1720);
    ctx.fillStyle = COLORS.soft;
    ctx.font = '600 40px Montserrat, sans-serif';
    ctx.fillText(`sur ${data.link}`, MARGIN, 1778);
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  private wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';
    for (let i = 0; i < words.length; i += 1) {
      const word = words[i];
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width > maxWidth && current) {
        lines.push(current);
        current = word;
        // On the last allowed line, keep the rest of the words and ellipsize.
        if (lines.length === maxLines - 1) {
          const remaining = [current, ...words.slice(i + 1)].join(' ');
          lines.push(this.ellipsize(ctx, remaining, maxWidth));
          return lines;
        }
      } else {
        current = candidate;
      }
    }
    if (current) {
      lines.push(this.ellipsize(ctx, current, maxWidth));
    }
    return lines;
  }

  private ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (ctx.measureText(text).width <= maxWidth) {
      return text;
    }
    let truncated = text;
    while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return `${truncated.trim()}…`;
  }

  private slug(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'activite';
  }
}
